"use client";

import { Suspense, useState, useEffect } from "react";
import Link from "next/link";
import { useRouter, useSearchParams } from "next/navigation";
import { authClient } from "@/lib/auth-client";
import { User, Lock, Eye, EyeOff } from "lucide-react";

function GoogleIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" aria-hidden>
      <path
        fill="#4285F4"
        d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
      />
      <path
        fill="#34A853"
        d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
      />
      <path
        fill="#FBBC05"
        d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
      />
      <path
        fill="#EA4335"
        d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
      />
    </svg>
  );
}

function LoginForm() {
  const router = useRouter();
  const searchParams = useSearchParams();
  const rawCallback = searchParams.get("callbackUrl") || "/dashboard";
  const callbackUrl = rawCallback.startsWith("/") && !rawCallback.startsWith("//") ? rawCallback : "/dashboard";
  const [identifier, setIdentifier] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [loadingGoogle, setLoadingGoogle] = useState(false);
  const [forgotPasswordOpen, setForgotPasswordOpen] = useState(false);
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotSent, setForgotSent] = useState(false);

  // Afficher les erreurs OAuth renvoyées par le callback (ex: state_not_found, no_code)
  useEffect(() => {
    const errorParam = searchParams.get("error");
    const descParam = searchParams.get("error_description");
    if (errorParam) {
      const msg = descParam
        ? decodeURIComponent(descParam)
        : errorParam === "state_not_found"
          ? "Session de connexion Google expirée ou invalide. Réessayez."
          : errorParam === "no_code"
            ? "Google n’a pas renvoyé de code. Réessayez."
            : `Erreur de connexion : ${errorParam}`;
      setError(msg);
    }
  }, [searchParams]);

  // Clear potential conflicting cookies on mount to solve redirect Ioop
  useEffect(() => {
    const cleanSession = async () => {
      // 1. Attempt to sign out using the library (cleans server state + default cookies)
      try {
        await authClient.signOut();
      } catch (e) {
        // ignore error if already signed out
      }

      // Helper to delete cookie with various domain/path permutations
      const deleteCookie = (name: string) => {
        const domains = [
          window.location.hostname,
          `.${window.location.hostname}`,
          window.location.hostname.split('.').slice(-2).join('.'), // .coraia.eu
          `.${window.location.hostname.split('.').slice(-2).join('.')}`
        ];

        const paths = ['/', '/api', '/api/auth'];

        domains.forEach(d => {
          paths.forEach(p => {
            document.cookie = `${name}=; Path=${p}; Domain=${d}; Expires=Thu, 01 Jan 1970 00:00:01 GMT;`;
          });
        });

        // Also try without domain
        paths.forEach(p => {
          document.cookie = `${name}=; Path=${p}; Expires=Thu, 01 Jan 1970 00:00:01 GMT;`;
        });
      };

      // List of cookies to clear
      const cookiesToClear = [
        "axia_session_v2",
        "__Secure-axia_session_v2",
        "better-auth.session_token",
        "__Secure-better-auth.session_token",
        "better-auth.session_data",
        "better-auth.csrf_token"
      ];

      cookiesToClear.forEach(deleteCookie);
    };

    cleanSession();
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      const isEmail = identifier.includes("@");
      const result = isEmail
        ? await authClient.signIn.email({ email: identifier, password })
        : await authClient.signIn.username({ username: identifier, password });

      if (result.error) {
        setError(result.error.message || "Identifiant ou mot de passe incorrect");
      } else {
        router.push(callbackUrl as Parameters<typeof router.push>[0]);
        router.refresh();
      }
    } catch (err) {
      setError("Une erreur est survenue");
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!forgotEmail.trim()) return;
    setForgotLoading(true);
    setForgotSent(false);
    try {
      const base = typeof window !== "undefined" ? window.location.origin : "";
      const result = await authClient.requestPasswordReset({
        email: forgotEmail.trim(),
        redirectTo: `${base}/reset-password`,
      });
      if (result?.error) {
        setError(result.error.message ?? "Une erreur est survenue.");
        return;
      }
      setForgotSent(true);
      setError("");
    } catch {
      setError("Impossible d'envoyer le lien. Réessayez.");
    } finally {
      setForgotLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    setError("");
    setLoadingGoogle(true);
    try {
      const result = await authClient.signIn.social({
        provider: "google",
        callbackURL: callbackUrl,
      });
      // Si le serveur renvoie une erreur (ex. Google non configuré), l'afficher
      if (result?.error) {
        setError(result.error.message || "Connexion Google impossible. Vérifiez la configuration.");
        return;
      }
      // Redirection manuelle si le plugin n'a pas redirigé (sécurité)
      if (result?.data?.url && typeof window !== "undefined") {
        window.location.href = result.data.url;
        return;
      }
    } catch {
      setError("Connexion Google impossible. Vérifiez la configuration.");
    } finally {
      setLoadingGoogle(false);
    }
  };

  return (
    <div className="card p-8">
      <div className="text-center mb-8">
        <div className="flex justify-center mb-4">
          <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-primary-600 text-white font-bold text-xl">
            A
          </div>
        </div>
        <h1 className="text-2xl font-bold text-slate-900">Connexion</h1>
        <p className="mt-2 text-sm text-slate-500">Accédez à votre espace AXIA</p>
      </div>

      {error && (
        <div className="mb-6 rounded-lg bg-error-50 border border-error-200 p-4 text-sm text-error-700">
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-5">
        <div>
          <label htmlFor="identifier" className="block text-sm font-medium text-slate-700 mb-1.5">
            Email ou nom d&apos;utilisateur
          </label>
          <div className="relative">
            <User className="absolute left-3.5 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
            <input
              id="identifier"
              type="text"
              inputMode="email"
              autoComplete="username"
              required
              value={identifier}
              onChange={(e) => setIdentifier(e.target.value.trim())}
              className="input pl-11"
              placeholder="vous@exemple.com ou lucca"
              disabled={loading}
            />
          </div>
        </div>

        <div>
          <label htmlFor="password" className="block text-sm font-medium text-slate-700 mb-1.5">
            Mot de passe
          </label>
          <div className="relative">
            <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
            <input
              id="password"
              type={showPassword ? "text" : "password"}
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="input pl-11 pr-11"
              placeholder="••••••••"
              disabled={loading}
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
            >
              {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
            </button>
          </div>
        </div>

        <button type="submit" className="btn-primary w-full" disabled={loading}>
          {loading ? "Connexion..." : "Se connecter"}
        </button>

        <div className="relative my-4">
          <div className="absolute inset-0 flex items-center">
            <span className="w-full border-t border-slate-200" />
          </div>
          <div className="relative flex justify-center text-xs">
            <span className="bg-white px-2 text-slate-500">ou</span>
          </div>
        </div>

        <button
          type="button"
          onClick={handleGoogleSignIn}
          disabled={loadingGoogle}
          className="flex w-full items-center justify-center gap-3 rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm font-medium text-slate-700 shadow-sm transition-colors hover:bg-slate-50 disabled:opacity-50"
        >
          <GoogleIcon className="h-5 w-5" />
          {loadingGoogle ? "Connexion..." : "Se connecter avec Google"}
        </button>
      </form>

      <p className="mt-6 text-center text-sm text-slate-500">
        <button
          type="button"
          onClick={() => setForgotPasswordOpen(true)}
          className="text-primary-600 hover:text-primary-700 font-medium"
        >
          Mot de passe oublié ?
        </button>
      </p>
      <p className="mt-2 text-center text-sm text-slate-500">
        Pas de compte ?{" "}
        <Link href="/register" className="font-semibold text-primary-600 hover:text-primary-700">
          S&apos;inscrire
        </Link>
      </p>

      {/* Popup Mot de passe oublié */}
      {forgotPasswordOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" onClick={() => !forgotLoading && setForgotPasswordOpen(false)}>
          <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-6 space-y-4" onClick={(e) => e.stopPropagation()}>
            <h2 className="text-lg font-semibold text-slate-900">Mot de passe oublié</h2>
            {!forgotSent ? (
              <form onSubmit={handleForgotPasswordSubmit} className="space-y-4">
                <p className="text-sm text-slate-600">
                  Saisissez votre adresse email. Un lien de réinitialisation vous sera envoyé.
                </p>
                <input
                  type="email"
                  value={forgotEmail}
                  onChange={(e) => setForgotEmail(e.target.value)}
                  placeholder="vous@exemple.com"
                  className="w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm"
                  required
                  disabled={forgotLoading}
                />
                <div className="flex gap-2 justify-end">
                  <button
                    type="button"
                    onClick={() => setForgotPasswordOpen(false)}
                    className="rounded-xl border border-slate-200 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
                  >
                    Annuler
                  </button>
                  <button
                    type="submit"
                    disabled={forgotLoading}
                    className="rounded-xl bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700 disabled:opacity-50"
                  >
                    {forgotLoading ? "Envoi…" : "Envoyer le lien"}
                  </button>
                </div>
              </form>
            ) : (
              <>
                <p className="text-sm text-slate-600">
                  Si un compte existe pour cette adresse, un email avec un lien de réinitialisation a été envoyé. Consultez votre boîte de réception (et les spams).
                </p>
                <button
                  type="button"
                  onClick={() => { setForgotPasswordOpen(false); setForgotSent(false); setForgotEmail(""); }}
                  className="rounded-xl bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700"
                >
                  Fermer
                </button>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default function LoginPage() {
  return (
    <Suspense fallback={<div className="card p-8 text-center text-slate-500">Chargement...</div>}>
      <LoginForm />
    </Suspense>
  );
}
